import React from "react";
import { Link } from "react-router-dom";
import {
	NavItem as NavGroupItem,
	NavGroupTrigger,
	NavChevron,
	NavMenuShell,
	NavMenuFace,
	NavMenuItemLink
} from "./NavbarStyledComponent";
import type { NavDestination, NavGroup } from "../../../constants/navigation";

/**
 * ============================================================================
 * ONE GROUP OF THE DESKTOP NAVIGATION — a WAI-ARIA menu button (D-068).
 * ============================================================================
 *
 * `role="menu"` with `role="menuitem"` children, because that is what the
 * owner specified and it is a documented APG pattern. THE COST IS ON RECORD:
 * a `menuitem` is announced as "menu item", not as "link", so a screen-reader
 * user loses the word that would tell them this navigates. The alternative —
 * APG's Disclosure Navigation pattern, a plain list of links behind a
 * `aria-expanded` button — keeps "link" and is what APG itself recommends for
 * site navigation. Both are conformant; this one was chosen, and the trade is
 * written down rather than discovered later.
 *
 * WHAT THE ROLE OBLIGES, once taken:
 *   · arrow keys move focus INSIDE the menu, so items carry `tabIndex={-1}`
 *     and focus is moved programmatically;
 *   · Tab must leave the whole group rather than walk it. That falls out of
 *     `tabIndex={-1}` for free — Tab from a -1 element goes to the next
 *     TABBABLE element in document order, which is the next trigger;
 *   · Escape closes and RETURNS FOCUS TO THE TRIGGER. Without the second half,
 *     closing the menu strands focus on a `display: none` element and the next
 *     Tab restarts from the top of the document. That is the keyboard trap
 *     this component is most likely to have, and it is silent.
 *
 * THE MENU IS NEVER CLOSED BY HIDING IT IN PLACE. `display: none` (see
 * NavMenuShell) is the only mechanism used, because `visibility`/`opacity`
 * would leave the items focusable behind a control that says
 * `aria-expanded="false"` — a keyboard user tabbing into links that are not
 * on screen. It also lets the markup render unconditionally, so all ten
 * destinations sit in the pre-rendered HTML of every route.
 *
 * HOVER OPENS IT, AND HOVER MUST NOT BE ABLE TO STEAL FOCUS. A mouse open
 * never calls `.focus()`; only the keyboard paths do. And hover is gated on
 * `pointerType === "mouse"`, because a touch tap fires `pointerenter`
 * immediately before `click` — ungated, the tap would open the menu and the
 * click would then close it, which is exactly how a dropdown becomes
 * unusable on a phone while testing fine on a laptop.
 */

interface NavDropdownProps {
	group: NavGroup;
	isOpen: boolean;
	/** Opens this group, closing whichever was open. */
	onOpen: (id: string) => void;
	/** Closes this group only if it is still the open one — see the leave timer. */
	onClose: (id: string) => void;
}

/** Where focus should land once the menu has opened. `null` = a mouse opened it. */
type FocusIntent = "first" | "last" | null;

/** Grace period on mouse-leave, so travelling from trigger to panel does not close it. */
const LEAVE_MS = 140;

const NavDropdown: React.FC<NavDropdownProps> = ({ group, isOpen, onOpen, onClose }: NavDropdownProps) => {
	const triggerRef = React.useRef<HTMLButtonElement | null>(null);
	const menuRef = React.useRef<HTMLUListElement | null>(null);
	const leaveTimer = React.useRef<number | null>(null);
	const [focusIntent, setFocusIntent] = React.useState<FocusIntent>(null);

	const triggerId = `nav-trigger-${group.id}`;
	const menuId = `nav-menu-${group.id}`;

	/**
	 * The items are read out of the DOM rather than collected into a ref array.
	 * Half of them render through `as={Link}`, so a ref array would have to be
	 * typed across two different element wrappers for no gain — the list is
	 * short, it is only read on a keystroke, and this cannot drift from what is
	 * actually rendered.
	 */
	const menuItems = React.useCallback(
		(): HTMLElement[] => Array.from(menuRef.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? []),
		[]
	);

	const clearLeaveTimer = () => {
		if (leaveTimer.current !== null) {
			window.clearTimeout(leaveTimer.current);
			leaveTimer.current = null;
		}
	};

	React.useEffect(() => clearLeaveTimer, []);

	/* Focus moves only after the menu is actually displayed — an element inside
	   `display: none` cannot take focus, so this cannot be done in the handler
	   that opens it. */
	React.useEffect(() => {
		if (!isOpen) {
			if (focusIntent !== null) setFocusIntent(null);
			return;
		}
		if (focusIntent === null) return;
		const items = menuItems();
		const target = focusIntent === "last" ? items[items.length - 1] : items[0];
		if (target) target.focus();
		setFocusIntent(null);
	}, [isOpen, focusIntent, menuItems]);

	const openWith = (intent: FocusIntent) => {
		clearLeaveTimer();
		setFocusIntent(intent);
		onOpen(group.id);
	};

	const closeAndReturnFocus = () => {
		clearLeaveTimer();
		onClose(group.id);
		if (triggerRef.current) triggerRef.current.focus();
	};

	const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		switch (event.key) {
			case "ArrowDown":
			case "Enter":
			case " ":
			case "Spacebar":
				/* preventDefault does two jobs: it stops Space scrolling the page,
				   and it stops the browser synthesising a click from Enter/Space —
				   which would run the toggle below and immediately undo this. */
				event.preventDefault();
				openWith("first");
				break;
			case "ArrowUp":
				event.preventDefault();
				openWith("last");
				break;
			case "Escape":
				if (isOpen) {
					event.preventDefault();
					closeAndReturnFocus();
				}
				break;
			default:
				break;
		}
	};

	/** Pointer only — Enter and Space are handled above and never reach this. */
	const onTriggerClick = () => {
		clearLeaveTimer();
		if (isOpen) onClose(group.id);
		else onOpen(group.id);
	};

	const onItemKeyDown = (event: React.KeyboardEvent<HTMLElement>, index: number) => {
		const items = menuItems();
		if (!items.length) return;

		switch (event.key) {
			case "ArrowDown": {
				event.preventDefault();
				const next = items[(index + 1) % items.length];
				if (next) next.focus();
				break;
			}
			case "ArrowUp": {
				event.preventDefault();
				const prev = items[(index - 1 + items.length) % items.length];
				if (prev) prev.focus();
				break;
			}
			case "Home": {
				event.preventDefault();
				items[0].focus();
				break;
			}
			case "End": {
				event.preventDefault();
				items[items.length - 1].focus();
				break;
			}
			case "Escape": {
				event.preventDefault();
				closeAndReturnFocus();
				break;
			}
			case " ":
			case "Spacebar": {
				/* A menuitem activates on Space as well as Enter. An anchor does
				   not, so it is activated explicitly; Enter needs nothing, the
				   anchor already handles it. */
				event.preventDefault();
				(event.currentTarget as HTMLElement).click();
				break;
			}
			default:
				break;
		}
	};

	/**
	 * Focus leaving the group closes it — this is what makes Tab exit cleanly
	 * without a Tab handler that would have to guess where focus was going.
	 * `relatedTarget` inside this element means focus merely moved between the
	 * trigger and an item, which is not a close.
	 */
	const onGroupBlur = (event: React.FocusEvent<HTMLLIElement>) => {
		if (!isOpen) return;
		const next = event.relatedTarget as Node | null;
		if (next && event.currentTarget.contains(next)) return;
		onClose(group.id);
	};

	return (
		<NavGroupItem
			onBlur={onGroupBlur}
			onPointerEnter={(event: React.PointerEvent<HTMLLIElement>) => {
				if (event.pointerType !== "mouse") return;
				clearLeaveTimer();
				onOpen(group.id);
			}}
			onPointerLeave={(event: React.PointerEvent<HTMLLIElement>) => {
				if (event.pointerType !== "mouse") return;
				clearLeaveTimer();
				leaveTimer.current = window.setTimeout(() => onClose(group.id), LEAVE_MS);
			}}
		>
			<NavGroupTrigger
				type="button"
				id={triggerId}
				ref={triggerRef}
				aria-haspopup="true"
				aria-expanded={isOpen}
				aria-controls={menuId}
				onClick={onTriggerClick}
				onKeyDown={onTriggerKeyDown}
			>
				{group.label}
				<NavChevron
					data-open={isOpen ? "true" : "false"}
					viewBox="0 0 10 6"
					fill="none"
					aria-hidden="true"
					focusable="false"
				>
					<path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
				</NavChevron>
			</NavGroupTrigger>

			<NavMenuShell data-open={isOpen ? "true" : "false"}>
				<NavMenuFace ref={menuRef} id={menuId} role="menu" aria-labelledby={triggerId}>
					{group.items.map((item: NavDestination, index: number) => (
						<li role="none" key={item.label}>
							{item.to ? (
								<NavMenuItemLink
									as={Link}
									to={item.to}
									role="menuitem"
									tabIndex={-1}
									onClick={() => onClose(group.id)}
									onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => onItemKeyDown(event, index)}
								>
									{item.label}
								</NavMenuItemLink>
							) : (
								<NavMenuItemLink
									href={item.href}
									role="menuitem"
									tabIndex={-1}
									onClick={() => onClose(group.id)}
									onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => onItemKeyDown(event, index)}
								>
									{item.label}
								</NavMenuItemLink>
							)}
						</li>
					))}
				</NavMenuFace>
			</NavMenuShell>
		</NavGroupItem>
	);
};

export default NavDropdown;
